import { FieldNumberOutlined } from '@ant-design/icons';
import React from 'react';

import type { ITableParams } from '@client/types/antd.type';
import type { ICommonDataType, IObjectId } from '@common/types';

/**
 * Creates a bounded LRU cache backed by a Map.
 * Map preserves insertion order — delete+re-insert on get promotes a key to the tail (MRU).
 * When size exceeds `max`, the head entry (LRU) is evicted.
 */
export const createBoundedLruCache = <K, V>(max: number) => {
  const map = new Map<K, V>();

  return {
    get(key: K): V | undefined {
      if (!map.has(key)) return undefined;
      const value = map.get(key) as V;
      map.delete(key);
      map.set(key, value);
      return value;
    },
    set(key: K, value: V): void {
      map.set(key, value);
      if (map.size > max) {
        const lruKey = map.keys().next().value as K | undefined;
        if (lruKey !== undefined) map.delete(lruKey);
      }
    },
    get size(): number {
      return map.size;
    },
  };
};

/**
 * Add an idx property to each item in the array and key property
 * for correct Table component rendering.
 *
 * @param {any[]} items - Array of objects
 * @returns {ICommonDataType[]} - Array of objects with idx and key properties
 */
export const indexable = <T extends IObjectId>(items: T[]): T[] =>
  items.map((item: T, idx: number) => ({
    ...item,
    idx: idx + 1,
    key: item.id,
  }));

export const indexColumn: ICommonDataType = {
  key: 'idx',
  title: (<FieldNumberOutlined />) as React.ReactNode,
  concealable: false,
  dataIndex: 'idx',
  rowScope: 'row',
  width: 70,
  fixed: 'left',
  align: 'center',
};

/**
 * Converts ITableParams to a query string object.
 * @param {ITableParams} params - The ITableParams object to convert.
 * @returns {Record<string, any>} - The query string object.
 */
export const qsTableParams = (params: ITableParams): Record<string, any> => ({
  results: params.pagination?.pageSize,
  page: params.pagination?.current,
  ...params,
});
